import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DocumentUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadProgress: (progress: number) => void;
}

const DocumentUploadModal = ({
  open,
  onOpenChange,
  onUploadProgress,
}: DocumentUploadModalProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndAddFiles(droppedFiles);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      validateAndAddFiles(selectedFiles);
    }
  };

  const validateAndAddFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const isValidType = ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      return isValidType && isValidSize;
    });

    if (validFiles.length !== newFiles.length) {
      toast({
        variant: "destructive",
        title: "Invalid files",
        description: "Some files were rejected. Please ensure all files are PDF, JPG, or PNG and under 5MB.",
      });
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    let progress = 0;

    try {
      // Simulate upload progress
      const interval = setInterval(() => {
        progress += 10;
        if (progress > 100) {
          clearInterval(interval);
          setUploading(false);
          onOpenChange(false);
          setFiles([]);
          toast({
            title: "Upload Complete",
            description: "Your documents have been uploaded successfully.",
          });
        }
        onUploadProgress(progress);
      }, 500);
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: "There was an error uploading your documents. Please try again.",
      });
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogDescription>
            Drag and drop your documents or click to browse. Accepted formats: PDF, JPG, PNG (max 5MB)
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "mt-4 p-8 border-2 border-dashed rounded-lg text-center cursor-pointer",
            "hover:border-primary/50 transition-colors",
            uploading && "pointer-events-none opacity-50"
          )}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            className="hidden"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileInput}
            disabled={uploading}
          />
          <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            Drop your files here or click to browse
          </p>
        </div>

        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-accent/5 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <FileIcon type={file.type} />
                  <span className="text-sm truncate">{file.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {uploading && (
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Uploading...</span>
              <span>50%</span>
            </div>
            <Progress value={50} className="h-2" />
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FileIcon = ({ type }: { type: string }) => {
  let icon = <FileText className="h-4 w-4 text-muted-foreground" />;
  
  if (type.includes('pdf')) {
    icon = <FileText className="h-4 w-4 text-red-500" />;
  } else if (type.includes('image')) {
    icon = <FileText className="h-4 w-4 text-blue-500" />;
  }
  
  return icon;
};

export default DocumentUploadModal;